import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import { FeatureFlagProvider, useFeatureFlags } from "@/context/FeatureFlagContext";
import PortalLayout from "@/components/layout/PortalLayout";
import { Spinner } from "@/components/common";
import { Toaster } from "@/components/ui/sonner";
import ThemePreviewToggle from "@/components/ThemePreviewToggle";
import ParentDashboardV2Preview from "@/pages/preview/ParentDashboardV2";
import { ScheduleV2Preview, InvoicesV2Preview, MessagesV2Preview, MoreV2Preview } from "@/pages/preview/ParentScreensV2";
import FeatureDisabledPage from "@/pages/FeatureDisabledPage";

// Public
import {
  LandingPage, PublicCourses, PublicTeachers, EnquiryPage, ConfirmationPage,
} from "@/pages/public/PublicSite";
import TrialBookingPage from "@/pages/public/TrialBookingPage";
import PublicTeacherProfile from "@/pages/public/TeacherProfilePage";
import CalendarPage from "@/pages/public/CalendarPage";
import RegisterPage from "@/pages/public/RegisterPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AccountClaimPage from "@/pages/public/AccountClaimPage";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { StudentsPage, StudentDetailPage } from "@/pages/admin/StudentsPage";
import { ParentsPage, ParentDetailPage, TeachersPage, TeacherDetailPage } from "@/pages/admin/PeoplePages";
import { CoursesPage, CourseFeesPage, StudiosPage, FeeSettingsPage } from "@/pages/admin/CatalogPages";
import StudioReservationsPage from "@/pages/admin/StudioReservationsPage";
import CoursesBoardPage from "@/pages/admin/CoursesBoardPage";
import CoursesTreePage from "@/pages/admin/CoursesTreePage";
import CoursesAuditPage from "@/pages/admin/CoursesAuditPage";
import DanglingRefsPage from "@/pages/admin/DanglingRefsPage";
import { SchedulesPage, BookingsPage } from "@/pages/admin/SchedulingPages";
import { InvoicesPage, PaymentsPage } from "@/pages/admin/BillingPages";
import { AttendancePage, RemarksPage } from "@/pages/admin/LessonPages";
import { ApprovalsPage, EnquiriesPage } from "@/pages/admin/ApprovalsPage";
import PayrollPage from "@/pages/admin/PayrollPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import DataImportPage from "@/pages/admin/DataImportPage";
import AccountClaimsPage from "@/pages/admin/AccountClaimsPage";
import {
  AdminTeacherKpiList, AdminTeacherKpiDetail, AdminTeacherReview, KpiSettingsPage,
} from "@/pages/admin/TeacherKpiAdminPages";
import RecommendationsPage from "@/pages/admin/RecommendationPages";
import {
  GrowthDashboard, GrowthTeacherProfiles, GrowthTeacherProfileEditor,
  GrowthTestimonials, GrowthEnquiries, GrowthEnrolments,
} from "@/pages/admin/GrowthPages";
import {
  GrowthKnowledge, GrowthFaqs, GrowthConversations, GrowthEscalations,
} from "@/pages/admin/GrowthAiPages";

// Teacher
import {
  TeacherDashboard, TeacherToday, TeacherTimetable, TeacherStudents, TeacherClassDetail,
  TeacherAttendance, TeacherRemarks, TeacherPayroll, TeacherProfile,
} from "@/pages/teacher/TeacherPages";
import TeacherKpiPage, { TeacherRewardsPage, TeacherRankingPage } from "@/pages/teacher/KpiPages";

// Parent
import {
  ParentDashboard, ParentChildren, ParentTimetable, ParentAttendance, ParentRemarks,
  ParentInvoices, ParentPaymentStatus, ParentNotifications,
} from "@/pages/parent/ParentPages";

// Advanced reschedule module
import { RescheduleListPage, RescheduleDetailPage } from "@/pages/reschedule/ReschedulePages";
import {
  ReschedulePolicyPage, AdminRescheduleRequests, AdminRescheduleApproval,
} from "@/pages/admin/RescheduleAdminPages";
import LeaveRescheduleDashboard from "@/pages/admin/LeaveRescheduleDashboard";
import { NotificationCenterPage } from "@/pages/admin/NotificationAdminPages";
import { LeaveListPage, AdminLeavePage } from "@/pages/operations/LeavePages";
import TeacherAvailabilityPage from "@/pages/teacher/AvailabilityPage";
import WalkInEnrolPage from "@/pages/admin/WalkInEnrolPage";
import FrontDeskPage from "@/pages/admin/FrontDeskPage";
import FindSlotPage from "@/pages/admin/FindSlotPage";
import TeachingRequestPage from "@/pages/teacher/TeachingRequestPage";
import AdminTeachingRequestsPage from "@/pages/admin/TeachingRequestsAdminPage";
import AdminAvailabilityRequestsPage from "@/pages/admin/AvailabilityRequestsAdminPage";
import SchoolHolidaysAdminPage from "@/pages/admin/SchoolHolidaysAdminPage";
import TeacherCalendarPage from "@/pages/teacher/TeacherCalendarPage";
import ReplacementAssistantPage from "@/pages/teacher/ReplacementAssistantPage";
import FeatureControlPage from "@/pages/admin/FeatureControlPage";
import WhatsAppTestCentrePage from "@/pages/admin/WhatsAppTestCentrePage";
import SiteSettingsPage from "@/pages/admin/SiteSettingsPage";
import BillplzResultPage from "@/pages/parent/BillplzResultPage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";

// Role-gated layout wrapper (with per-route feature-flag guard)
function Protected({ roles }) {
  const { user } = useAuth();
  const { isRouteBlocked } = useFeatureFlags();
  const location = useLocation();
  if (user === null) return <Spinner full />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  const blocked = isRouteBlocked(location.pathname);
  return (
    <PortalLayout>
      {blocked ? <FeatureDisabledPage /> : <Outlet />}
    </PortalLayout>
  );
}

const STAFF = ["super_admin", "admin", "finance"];
const ADMINS = ["super_admin", "admin"];

function AdminTeacherCalendarShim() {
  const { teacherId } = useParams();
  return <TeacherCalendarPage adminMode={true} teacherIdOverride={teacherId} />;
}

function App() {
  return (
    <AuthProvider>
      <FeatureFlagProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
          <ThemePreviewToggle />
        </BrowserRouter>
      </FeatureFlagProvider>
    </AuthProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth return: process session_id BEFORE any route/auth checks run
  if (location.hash?.includes("session_id=") && location.pathname !== "/claim") return <AuthCallback />;
  return (
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/courses" element={<PublicCourses />} />
          <Route path="/teachers" element={<PublicTeachers />} />
          <Route path="/teachers/:slug" element={<PublicTeacherProfile />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/trial-booking" element={<TrialBookingPage />} />
          <Route path="/enquiry" element={<EnquiryPage />} />
          <Route path="/confirmation" element={<ConfirmationPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/claim" element={<AccountClaimPage />} />

          {/* Admin / Finance portal */}
          <Route element={<Protected roles={STAFF} />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/students" element={<StudentsPage />} />
            <Route path="/admin/students/new" element={<StudentsPage autoOpenCreate />} />
            <Route path="/admin/students/:id" element={<StudentDetailPage />} />
            <Route path="/admin/walk-in-enrol" element={<WalkInEnrolPage />} />
            <Route path="/admin/front-desk" element={<FrontDeskPage />} />
            <Route path="/admin/find-slot" element={<FindSlotPage />} />
            <Route path="/admin/parents" element={<ParentsPage />} />
            <Route path="/admin/parents/:id" element={<ParentDetailPage />} />
            <Route path="/admin/teachers" element={<TeachersPage />} />
            <Route path="/admin/teachers/new" element={<TeachersPage autoOpenCreate />} />
            <Route path="/admin/teachers/:id" element={<TeacherDetailPage />} />
            <Route path="/admin/courses" element={<CoursesTreePage />} />
            <Route path="/admin/courses-audit" element={<CoursesAuditPage />} />
            <Route path="/admin/dangling-refs" element={<DanglingRefsPage />} />
            <Route path="/admin/courses-board" element={<CoursesBoardPage />} />
            <Route path="/admin/courses-list" element={<CoursesPage />} />
            <Route path="/admin/course-fees" element={<CoursesTreePage />} />
            <Route path="/admin/course-fees-table" element={<CourseFeesPage />} />
            <Route path="/admin/fee-settings" element={<FeeSettingsPage />} />
            <Route path="/admin/sst-settings" element={<FeeSettingsPage />} />
            <Route path="/admin/settings" element={<FeeSettingsPage />} />
            <Route path="/admin/studios" element={<StudiosPage />} />
            <Route path="/admin/studio-reservations" element={<StudioReservationsPage />} />
            <Route path="/admin/schedules" element={<SchedulesPage />} />
            <Route path="/admin/bookings" element={<BookingsPage />} />
            <Route path="/admin/calendar" element={<BookingsPage defaultTab="calendar" />} />
            <Route path="/admin/invoices" element={<InvoicesPage />} />
            <Route path="/admin/payments" element={<PaymentsPage />} />
            <Route path="/admin/attendance" element={<AttendancePage />} />
            <Route path="/admin/lesson-remarks" element={<RemarksPage />} />
            <Route path="/admin/requests" element={<AdminRescheduleRequests />} />
            <Route path="/admin/approvals" element={<ApprovalsPage />} />
            <Route path="/admin/reschedule-policy" element={<ReschedulePolicyPage />} />
            <Route path="/admin/reschedule-requests" element={<AdminRescheduleRequests />} />
            <Route path="/admin/reschedule-approval/:id" element={<AdminRescheduleApproval />} />
            <Route path="/admin/leave-reschedule" element={<LeaveRescheduleDashboard />} />
            <Route path="/admin/notification-center" element={<NotificationCenterPage />} />
            <Route path="/admin/leave-requests" element={<AdminLeavePage />} />
            <Route path="/admin/enquiries" element={<EnquiriesPage />} />
            <Route path="/admin/payroll" element={<PayrollPage />} />
            <Route path="/admin/payroll/settings" element={<PayrollPage defaultTab="settings" />} />
            <Route path="/admin/payroll/generate" element={<PayrollPage defaultTab="generate" />} />
            <Route path="/admin/payroll/approval" element={<PayrollPage defaultTab="runs" />} />
            <Route path="/admin/payroll/reports" element={<PayrollPage defaultTab="report" />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
            <Route path="/admin/data-import" element={<DataImportPage />} />
            <Route path="/admin/account-claims" element={<AccountClaimsPage />} />
            <Route path="/admin/teacher-kpi" element={<AdminTeacherKpiList />} />
            <Route path="/admin/teacher-kpi/:id" element={<AdminTeacherKpiDetail />} />
            <Route path="/admin/teacher-review/:id" element={<AdminTeacherReview />} />
            <Route path="/admin/kpi-settings" element={<KpiSettingsPage />} />
            <Route path="/admin/recommendations" element={<RecommendationsPage />} />
            <Route path="/admin/teaching-requests" element={<AdminTeachingRequestsPage />} />
            <Route path="/admin/availability-requests" element={<AdminAvailabilityRequestsPage />} />
            <Route path="/admin/school-holidays" element={<SchoolHolidaysAdminPage />} />
            <Route path="/admin/teachers/:teacherId/calendar" element={<AdminTeacherCalendarShim />} />
          </Route>

          {/* Super Admin only — Feature Control */}
          <Route element={<Protected roles={["super_admin"]} />}>
            <Route path="/admin/feature-control" element={<FeatureControlPage />} />
            <Route path="/admin/whatsapp-test-centre" element={<WhatsAppTestCentrePage />} />
            <Route path="/admin/site-settings" element={<SiteSettingsPage />} />
          </Route>

          {/* Available to all authenticated users */}
          <Route element={<Protected roles={["super_admin", "admin", "finance", "teacher", "parent", "student"]} />}>
            <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
          </Route>

          {/* Growth module — admin/owner only (finance excluded by default) */}
          <Route element={<Protected roles={ADMINS} />}>
            <Route path="/admin/growth" element={<GrowthDashboard />} />
            <Route path="/admin/growth/teacher-profiles" element={<GrowthTeacherProfiles />} />
            <Route path="/admin/growth/teacher-profiles/:teacherId" element={<GrowthTeacherProfileEditor />} />
            <Route path="/admin/growth/testimonials" element={<GrowthTestimonials />} />
            <Route path="/admin/growth/enquiries" element={<GrowthEnquiries />} />
            <Route path="/admin/growth/enrolments" element={<GrowthEnrolments />} />
            <Route path="/admin/growth/ai-knowledge" element={<GrowthKnowledge />} />
            <Route path="/admin/growth/ai-faqs" element={<GrowthFaqs />} />
            <Route path="/admin/growth/ai-conversations" element={<GrowthConversations />} />
            <Route path="/admin/growth/ai-escalations" element={<GrowthEscalations />} />
          </Route>

          {/* Teacher portal */}
          <Route element={<Protected roles={["teacher"]} />}>
            <Route path="/teacher/dashboard" element={<TeacherDashboard />} />
            <Route path="/teacher/calendar" element={<TeacherCalendarPage />} />
            <Route path="/teacher/today" element={<TeacherToday />} />
            <Route path="/teacher/timetable" element={<TeacherTimetable />} />
            <Route path="/teacher/students" element={<TeacherStudents />} />
            <Route path="/teacher/classes/:id" element={<TeacherClassDetail />} />
            <Route path="/teacher/attendance" element={<TeacherAttendance />} />
            <Route path="/teacher/lesson-remarks" element={<TeacherRemarks />} />
            <Route path="/teacher/reschedule" element={<RescheduleListPage role="teacher" />} />
            <Route path="/teacher/replacement-assistant" element={<ReplacementAssistantPage />} />
            <Route path="/teacher/reschedule/:id" element={<RescheduleDetailPage role="teacher" />} />
            <Route path="/teacher/leave" element={<LeaveListPage role="teacher" />} />
            <Route path="/teacher/availability" element={<TeacherAvailabilityPage />} />
            <Route path="/teacher/payroll" element={<TeacherPayroll />} />
            <Route path="/teacher/payroll/history" element={<TeacherPayroll />} />
            <Route path="/teacher/kpi" element={<TeacherKpiPage />} />
            <Route path="/teacher/rewards" element={<TeacherRewardsPage />} />
            <Route path="/teacher/ranking" element={<TeacherRankingPage />} />
            <Route path="/teacher/profile" element={<TeacherProfile />} />
            <Route path="/teacher/teaching-requests" element={<TeachingRequestPage />} />
          </Route>

          {/* Parent portal — Cornerstone v2 design (was ParentDashboard/Timetable/Notifications/Invoices) */}
          <Route element={<Protected roles={["parent"]} />}>
            <Route path="/parent/dashboard" element={<ParentDashboardV2Preview />} />
            <Route path="/parent/children" element={<ParentChildren />} />
            <Route path="/parent/timetable" element={<ScheduleV2Preview />} />
            <Route path="/parent/attendance" element={<ParentAttendance />} />
            <Route path="/parent/lesson-remarks" element={<ParentRemarks />} />
            <Route path="/parent/invoices" element={<InvoicesV2Preview />} />
            <Route path="/parent/payment-status" element={<ParentPaymentStatus />} />
            <Route path="/parent/reschedule" element={<RescheduleListPage role="parent" />} />
            <Route path="/parent/reschedule/:id" element={<RescheduleDetailPage role="parent" />} />
            <Route path="/parent/leave" element={<LeaveListPage role="parent" />} />
            <Route path="/parent/notifications" element={<MessagesV2Preview />} />
            <Route path="/parent/payments/billplz/result" element={<BillplzResultPage />} />
            {/* v1 fallback still accessible for A/B comparison */}
            <Route path="/parent/dashboard-v1" element={<ParentDashboard />} />
            <Route path="/parent/timetable-v1" element={<ParentTimetable />} />
            <Route path="/parent/invoices-v1" element={<ParentInvoices />} />
            <Route path="/parent/notifications-v1" element={<ParentNotifications />} />
            {/* keep preview aliases so old bookmarks still work */}
            <Route path="/preview/parent" element={<ParentDashboardV2Preview />} />
            <Route path="/preview/parent/schedule" element={<ScheduleV2Preview />} />
            <Route path="/preview/parent/invoices" element={<InvoicesV2Preview />} />
            <Route path="/preview/parent/messages" element={<MessagesV2Preview />} />
            <Route path="/preview/parent/more" element={<MoreV2Preview />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

export default App;
